// src/components/modals/ConfiguracionModal.jsx
//
// Modal "Configuración": dos secciones (CUENTA + PRIVACIDAD).
//
// Acciones:
//   - Nombre              → click reemplaza row con input editable.
//                           Save: updateDoc /users/{uid} { nombre }.
//                           Toast inline 2s.
//   - Cambiar contraseña  → resetPassword(user.email) + toast inline.
//   - Conexión Telegram   → navigate('/conectar-telegram').
//   - Términos            → navigate('/terminos').
//   - Política privacidad → navigate('/privacidad').
//
// Toast inline: mensaje breve dentro del modal por 2s, sin sistema
// global de notificaciones (overhead innecesario para esto).

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getFirestore } from 'firebase/firestore';
import app from '../../firebase';
import { useAuth } from '../../auth/AuthContext';
import { resetPassword } from '../../auth/authHelpers';

const db = getFirestore(app);

const I = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IconClose = () => <I size={18}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></I>;
const IconUser = () => <I><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></I>;
const IconKey = () => <I><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></I>;
const IconDoc = () => <I><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></I>;
const IconShield = () => <I><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></I>;
const IconChev = () => <I size={14}><polyline points="9 18 15 12 9 6"/></I>;

function ActionRow({ icon, title, subtitle, onClick }) {
  return (
    <button className="dm-action-row" onClick={onClick} type="button">
      <span className="ico">{icon}</span>
      <div className="body">
        <p className="t">{title}</p>
        {subtitle && <p className="d">{subtitle}</p>}
      </div>
      <span className="chev"><IconChev /></span>
    </button>
  );
}

export default function ConfiguracionModal({ open, onClose }) {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'ok'|'err', msg }

  // ESC cierra (sólo si no estamos editando nombre — ESC en input limpia primero)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (editingName) {
          setEditingName(false);
        } else {
          onClose?.();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, editingName]);

  // Auto-dismiss toast a los 2.5s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  if (!open) return null;

  const nombreActual =
    userData?.nombre ||
    user?.displayName ||
    (user?.email ? user.email.split('@')[0] : '');
  const email = userData?.email || user?.email || '';

  const startEditName = () => {
    setNameInput(nombreActual);
    setEditingName(true);
  };

  const saveName = async () => {
    const newName = (nameInput || '').trim();
    if (!newName || newName === nombreActual) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { nombre: newName });
      setEditingName(false);
      setToast({ type: 'ok', msg: 'Nombre actualizado' });
    } catch (err) {
      console.error('[ConfiguracionModal] saveName error:', err);
      setToast({ type: 'err', msg: 'No pudimos guardar el nombre. Intentá de nuevo.' });
    } finally {
      setSavingName(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      await resetPassword(email);
      setToast({ type: 'ok', msg: 'Te enviamos un email para restablecer tu contraseña' });
    } catch (err) {
      console.error('[ConfiguracionModal] resetPassword error:', err);
      setToast({ type: 'err', msg: 'No pudimos enviar el email. Intentá de nuevo.' });
    }
  };

  const handleTerms = () => { onClose?.(); navigate('/terminos'); };
  const handlePrivacy = () => { onClose?.(); navigate('/privacidad'); };

  return createPortal(
    <div
      className="dm-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="dm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dm-cfg-title"
      >
        <div className="dm-modal-header">
          <h2 className="dm-modal-title" id="dm-cfg-title">Configuración</h2>
          <button className="dm-modal-close" onClick={onClose} aria-label="Cerrar" type="button">
            <IconClose />
          </button>
        </div>

        {/* CUENTA */}
        <div className="dm-section-label">Cuenta</div>
        <div className="dm-card">
          {/* Nombre — toggle entre row clickeable e inline edit */}
          {editingName ? (
            <div className="dm-inline-edit">
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
                autoFocus
                disabled={savingName}
                placeholder="Tu nombre"
              />
              <button
                type="button"
                className="save"
                onClick={saveName}
                disabled={savingName}
              >
                {savingName ? '…' : 'Guardar'}
              </button>
              <button
                type="button"
                className="cancel"
                onClick={() => setEditingName(false)}
                disabled={savingName}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <ActionRow
              icon={<IconUser />}
              title="Nombre"
              subtitle={nombreActual}
              onClick={startEditName}
            />
          )}

          <ActionRow
            icon={<IconKey />}
            title="Cambiar contraseña"
            subtitle="Te enviamos un email para restablecerla"
            onClick={handleResetPassword}
          />
        </div>

        {/* PRIVACIDAD */}
        <div className="dm-section-label">Privacidad</div>
        <div className="dm-card">
          <ActionRow
            icon={<IconDoc />}
            title="Términos y condiciones"
            onClick={handleTerms}
          />
          <ActionRow
            icon={<IconShield />}
            title="Política de privacidad"
            onClick={handlePrivacy}
          />
        </div>

        {toast && (
          <div className={`dm-inline-toast ${toast.type}`} role="status">
            {toast.msg}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
